import { getCookie } from './cookies';
import { adler32 } from './hashes';
import { logDebug } from '~/utils/log';

export const validDataTimeout = 300000; // 5 minutes in ms
export const contextKey = 'shoppingContext';
export const userCookieName = 'ShoppingKey';
export const scheduledCleanupKey = 'shopping-bag-provider cache cleanup';

export interface StoredValue<T> {
  savedTimestamp: number;
  value: T;
  userCheck: number;
}

export const isValidTimestamp = (timestamp: number) => Date.now() - timestamp <= validDataTimeout;

// Both sessionStorage or localStorage could be used here.
// The sessionStorage doesn't support triggering syncing events on other browser tabs.
// The sessionStorage should clean up stored data after the browser tab gets closed.
// But the browser's restore-session-on-reopen functionality may clash with this.
// Both storage types have no connection with server-side session,
// so, if working with server session data, manual cleanups / refreshing
// has to be solved anyway.
export const storage = window.localStorage;
export const isStorageAvailable = Boolean(storage);
if (!isStorageAvailable) {
  logDebug('Storage is not available.');
}

// In Online session, there should always be some value set.
// The 'unknown' value is just a fallback for testing.
export const getUserCheck = () => adler32((getCookie(userCookieName) ?? '').trim() || 'unknown');

const parseValue = <T>(serializedData: string) => {
  let parsedValue: StoredValue<T> | undefined;
  try {
    parsedValue = JSON.parse(serializedData) as StoredValue<T>;
  } catch (error) {
    logDebug(`Error parsing serialized data:`, error);
  }

  return parsedValue;
};

export const getItem = <T>(key: string): T | undefined => {
  if (!isStorageAvailable) {
    return undefined;
  }

  const data = storage.getItem(key);
  if (!data) {
    logDebug(`Item with key '${key}' wasn't found in storage.`);

    return undefined;
  }

  const parsedValue = parseValue<T>(data);
  if (!parsedValue) {
    logDebug(`Error getting stored item with key '${key}'.`);

    return undefined;
  }

  const { savedTimestamp, value, userCheck } = parsedValue;
  if (!isValidTimestamp(savedTimestamp)) {
    storage.removeItem(key);
    logDebug(`Stored item with key '${key}' has expired and was deleted.`);

    return undefined;
  }

  if (userCheck !== getUserCheck()) {
    storage.removeItem(key);
    logDebug(`Item with key '${key}' was stored for different user and was deleted.`);

    return undefined;
  }

  logDebug(`Item with key ${key} was found in storage.\n\t`, parsedValue);

  return value;
};

export const setItem = <T>(key: string, value: T) => {
  if (!isStorageAvailable) {
    return false;
  }

  const data: StoredValue<T> = {
    savedTimestamp: Date.now(),
    value,
    userCheck: getUserCheck(),
  };

  try {
    storage.setItem(key, JSON.stringify(data));
    logDebug(`Stored item with key '${key}', value:\n\t`, data);
  } catch (error) {
    logDebug(`Error storing item with ${key}:`, error);

    return false;
  }

  return true;
};

export const removeItem = (key: string) => {
  try {
    storage.removeItem(key);
    logDebug(`Stored item with key '${key}' was removed.`);
  } catch (error) {
    logDebug(`Error removing item with key '${key}':`, error);

    return false;
  }

  return true;
};

export const isScheduledCacheCleanup = (): boolean => {
  if (!isStorageAvailable) {
    return false;
  }

  const isScheduled = Boolean(storage.getItem(scheduledCleanupKey));
  if (isScheduled) {
    logDebug('Cache cleanup was scheduled with last page reload.');
  }

  return isScheduled;
};

export const removeScheduledCacheCleanup = () => storage.removeItem(scheduledCleanupKey);

export const listenToStoredItemChanges = <T>(
  key: string,
  callback?: (value: T | undefined) => unknown,
) => {
  const eventName = 'storage';
  const eventListener = ((event: StorageEvent) => {
    if (event.storageArea !== storage || event.key !== key) {
      return undefined;
    }

    logDebug(
      `Storage change event from another browser tab was received for item with key '${key}'.`,
    );
    if (event.newValue === null) {
      logDebug(`Stored item with '${key}' was removed in another browser window.`);
      removeItem(key);

      return callback?.(undefined);
    }

    const parsedValue = parseValue<T>(event.newValue);
    if (!parsedValue) {
      return undefined;
    }

    try {
      storage.setItem(key, event.newValue);
      logDebug(`Stored item with key '${key}', value:\n\t`, parsedValue);
    } catch (error) {
      logDebug(`Error storing item with key '${key}':`, error);
    }

    return callback?.(parsedValue.value);
  }) as EventListener;

  return { eventName, eventListener };
};
